import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Text from '../../../../../elements/Typo/Text'
import AddFilters from './addFilters'
import CustomAgGrid from '../../../../../components/CustomAgGrid'
import Button from '../../../../../elements/button'

import { DataTableTypes, experimentTitle } from '../../../../../_config/types'
import { useParams } from 'react-router-dom'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import toast from 'react-hot-toast'
import { useExperimentSelector } from '../../../../../store/selectors/experimentSelector'
import { AgGridReact } from 'ag-grid-react'
import { useFpLoader } from '../../../../../store/api/hooks/fpLoaderHook'
import { useDialog } from '../../../../../store/api/hooks/dialogHook'
import Sql from '../../../../CreateExperiment/setScope/_components/processData/sql'
import { FALLBACK_TABS, FilterTab, proceedOrPromptToSaveQuery, resolveStartTab, resolveVisibleTabs } from '../../../../CreateExperiment/setScope/_components/processData/filterTabs'
import LazyNlToSqlFilter from '../../../../CreateExperiment/setScope/_components/processData/LazyNlToSqlFilter'
import Nl2sqlTabLayout from '../../../../CreateExperiment/setScope/_components/processData/Nl2sqlTabLayout'
import { useQuerySaveBlocker, useQuerySaveState } from '../../../../CreateExperiment/setScope/_components/processData/useChatQuerySave'
import { useChatFilterRestore } from '../../../../CreateExperiment/setScope/_components/processData/useChatFilterRestore'
import FilterTabButton from '../../../../CreateExperiment/setScope/_components/processData/FilterTabButton'
import DraftPreviewNotice from '../../../../CreateExperiment/setScope/_components/processData/DraftPreviewNotice'
import { useDraftPreview } from '../../../../CreateExperiment/setScope/_components/processData/useDraftPreview'
import { measurementFilterTabConfig } from '../../../../../_config/filterTabConfig'
import { useMeasurementConfigure } from '../../../../../_common/hook/useMeasurementConfigure'
import { changeToAddFilterTab, processDataColumnResponse, trackEvent } from '../../../../../_common/functions'
import { useComponentFunctions } from '../../../../../_common/hook/useComponentFunctions'
import Loader from '../../../../../components/loader'
import i18n from '../../../../../i18n'
import P from '../../../../../elements/Typo/P'
import RecurringFileUploadErrorModal from '../../../../../components/DataFileUploader/recurringFileUploadError.modal'

interface Props {
    dataType: 'Measurement' | 'Intervention' | 'Recommendation' | 'recurring'
    filterKey: 'measurement_table_filters' | 'intervention_table_filters' | 'recommendation_table_filters'
    conversionFlow: boolean
    literal?: string
    type?: string
    selectedDataTable?: any
    onRecurringFilter?: (data: any) => void
    next: () => void
    prev: () => void
    _for: experimentTitle
    experimentLiteral?: string
    onComplete?: (message: string) => void
    hidePreviousStep?: boolean
}

const ProcessData: React.FC<Props> = ({ dataType, filterKey, conversionFlow, literal, type, selectedDataTable, next, prev, _for, onRecurringFilter, experimentLiteral, onComplete, hidePreviousStep }) => {
    const { t } = useTranslation()
    const processDataGridRef = useRef<AgGridReact>(null)
    const processAdditionalDataGridRef = useRef<AgGridReact>(null)
    const params = useParams()
    const reduxExperiment = useExperimentSelector()
    const experimentApi = useExperimentApi()
    const [fetchedExperiment, setFetchedExperiment] = useState<any>(null)

    const expLiteral = experimentLiteral || params.experiment
    const [experiment, setExperiment] = useState<any>(experimentLiteral ? fetchedExperiment : reduxExperiment)

    useEffect(() => {
        if (experimentLiteral) {
            experimentApi.getExperimentDetailsWatch(
                { literal: experimentLiteral + '?use_orm=true' },
                (resp: any) => {
                    setFetchedExperiment(resp)
                    setExperiment(resp)
                },
                (error: any) => {
                    toast.error(error?.message || 'Failed to fetch experiment details')
                },
            )
        }
    }, [experimentLiteral])

    // The chat host (experimentLiteral) always shows the two existing tabs, whatever the product flags say.
    const nl2sqlBlocked = !!experiment?.user_design || dataType === 'recurring' || !!onRecurringFilter
    const visibleTabs = useMemo(() => resolveVisibleTabs(measurementFilterTabConfig, experimentLiteral ? FALLBACK_TABS : undefined, nl2sqlBlocked), [experimentLiteral, nl2sqlBlocked])
    const [filterTab, setFilterTab] = useState<FilterTab>(() => resolveStartTab(experiment, visibleTabs, filterKey))
    const [rowData, setRowData] = useState<any>(null)
    const [columnDefs, setcolumnDefs] = useState<[]>([])
    const { showLoader, hideLoader } = useFpLoader()
    const [columnPreviewData, setcolumnPreviewData] = useState<[]>([])
    const [filters, setFilters] = useState<any>(null)
    const [sqls, setSqls] = useState<any>(null)
    const [additionalRowData, setAdditionalRowData] = useState<any>(null)
    const [additionalcolumnDefs, setAdditionalcolumnDefs] = useState<[]>([])
    const [tableType, setTableType] = useState<DataTableTypes | null>(null)
    const [currentQueryExecuted, setCurrentQueryExecuted] = useState<boolean>(false)
    const [dataPreviewTable, setDataPreviewTable] = useState<any>(null)
    const [working, setWorking] = useState<boolean>(false)
    const [localLoading, setLocalLoading] = useState<boolean>(false)
    const [completed, setCompleted] = useState<boolean>(false)
    // True from the moment the chat tab applies a query until its preview refresh settles, so Next can't advance on stale data.
    const [chatApplying, setChatApplying] = useState<boolean>(false)
    const [recurringFileUploadError, setRecurringFileUploadError] = useState<any>(null)
    const dialog = useDialog()
    const sqlRef: any = useRef()
    // Set when the Next prompt was answered for a chat filter, so the route blocker does not ask again.
    const skipNextBlockerPromptRef = useRef(false)
    // Id of the latest preview request; a response for an older one is dropped so it can't overwrite newer rows, table and sqls.
    const previewRequestRef = useRef(0)
    const { draft, previewDraft, clearDraft, displayRows, displayColumns } = useDraftPreview((payload, onSuccess, onError) =>
        experimentApi.getFilterPreviwData({ literal: expLiteral, page: 1, per_page: 40, table_type: dataType === 'Measurement' ? 'KPI' : dataType, ...payload }, onSuccess, onError),
    )
    const {
        status: chatRestoreStatus,
        savedFilter: chatSavedFilter,
        reset: resetChatFilter,
        restore: restoreChatFilter,
    } = useChatFilterRestore({
        literal: expLiteral,
        filterKey,
        previewRequestRef,
        getExperimentDetails: experimentApi.getFilterRestoreDetails,
        preview: (payload, onSettled) => getFilterPreviwData(payload, false, onSettled),
        setFilters,
        // A canceled (reset or unmounted) preview never settles, so its loader must be released here.
        onCancel: () => stopLoading(),
    })
    const { currentSavedSql, chatSave, querySavePending, saveQuery, blocker } = useQuerySaveState({
        filterTab,
        filterKey,
        experiment,
        chatSavedFilter,
        sqls,
        currentQueryExecuted,
        sqlRef,
        experimentLiteral,
    })
    const measurementHookFunction = useMeasurementConfigure({ experimentType: 'RCT' })
    const componentFunctions = useComponentFunctions()
    const recurringFileUploadErrorModalRef: any = useRef()

    const startLoading = () => {
        if (experimentLiteral) {
            setLocalLoading(true)
        } else {
            showLoader()
        }
    }
    const stopLoading = () => {
        if (experimentLiteral) {
            setLocalLoading(false)
        } else {
            hideLoader()
        }
    }

    const handleNext = () => {
        if (onComplete) {
            setCompleted(true)
            onComplete('I have applied the measurement filters')
        } else {
            next()
        }
    }

    const [dataColumns, setDataColumns] = useState<string[] | null>(null)

    const getRecurringTableData = (payload: any = {}) => {
        startLoading()
        const _payload: any = {
            literal,
            page: 1,
            limit: 40,
            measurement_table_filters: payload,
            delta_measurement_table_name: selectedDataTable?.table?.name || selectedDataTable,
        }
        setRowData(null)
        experimentApi.getRecurringTableDataPreview(
            _payload,
            (data: any) => {
                console.log('recurring table data preview', data)
                if (dataType === 'recurring' && data?.data?.result?.length > 0) {
                    setDataColumns(Object.keys(data?.data?.result[0]) || [])
                }
                setDataPreviewTable(data?.data?.table_name)
                processDataColumnResponse(null, data.data.result, setcolumnDefs, setRowData, setcolumnPreviewData, stopLoading)
                setSqls(payload)
                stopLoading()
            },
            (error: any) => {
                stopLoading()
                setRowData([])
                if (error?.message) {
                    toast.error(error?.message, { duration: 10000 })
                } else {
                    toast.error(error, { duration: 10000 })
                }
            },
        )
    }

    const getFilterPreviwData = (payload: any = {}, markExecuted = true, onSettled?: (success: boolean) => void) => {
        const tableType = dataType === 'Measurement' ? 'KPI' : dataType

        if (tableType === 'recurring') {
            getRecurringTableData(payload)
            return
        }
        clearDraft()
        startLoading()
        setRowData(null)
        const requestId = ++previewRequestRef.current
        experimentApi.getFilterPreviwData(
            {
                literal: expLiteral,
                page: 1,
                per_page: 40,
                table_type: tableType,
                ...payload,
            },
            (data: any) => {
                if (requestId !== previewRequestRef.current) {
                    onSettled?.(false)
                    return
                }
                if (payload?.sql_query && markExecuted) {
                    setCurrentQueryExecuted(true)
                }
                processDataColumnResponse(experiment, data.data.result, setcolumnDefs, setRowData, setcolumnPreviewData, stopLoading)
                if (experiment?.data_mappings?.additional_measurement_table_name) {
                    componentFunctions.onAdditionalCsvUploaded('AdditionalMeasurement', expLiteral || '', setAdditionalcolumnDefs, setAdditionalRowData)
                }

                setDataPreviewTable(data?.data?.table_name)
                setSqls(payload)
                onSettled?.(true)
            },
            (error: any) => {
                if (requestId !== previewRequestRef.current) {
                    onSettled?.(false)
                    return
                }
                stopLoading()
                setRowData([])
                if (error?.message) {
                    toast.error(t(error?.message))
                } else {
                    toast.error(t(error))
                }
                onSettled?.(false)
            },
        )
    }

    const applyFilter = (filters: any) => {
        setFilters(filters)
        getFilterPreviwData(filters)
    }

    const applySQL = (SQL: any) => {
        trackEvent('measurement_sql_executed', { experiment_literal: expLiteral })
        getFilterPreviwData(SQL)
        setSqls(SQL)
    }

    const GenerateDataQualityReport = () => {
        if (sqls?.sql_query) {
            setWorking(true)

            experimentApi.generateDataQualityReport(
                {
                    literal: expLiteral,
                    query: sqls.sql_query,
                    upload_type: 'measurement',
                },
                (data: any) => {
                    setWorking(false)
                    handleNext()
                    console.log('data quality report generated', data)
                },
                (error: any) => {
                    setWorking(false)
                    toast.error(error?.message)
                },
            )
        } else {
            console.log('data quality report generated next without sql')
            handleNext()
        }
    }

    const updateExperimentCall = (payload: any) => {
        experimentApi.updateExperiment(
            payload,
            (data: any) => {
                if (sqls) {
                    GenerateDataQualityReport()
                } else {
                    console.log('experiment updated without sql  going to next step')
                    handleNext()
                }
                hideLoader()
            },
            (error: any) => {
                hideLoader()
                toast.error(error?.message)
            },
        )
    }

    const updateExperiment = () => {
        showLoader()
        const payload: any = {
            literal: expLiteral,
        }
        if (dataType === 'Measurement' || dataType === 'recurring') {
            payload.sql_measurement_table_name = dataPreviewTable
        }

        if (filters) {
            const filterval = filters?.filters
            payload[filterKey] = {
                filters: filterval,
            }
        }
        if (sqls?.sql_query) {
            payload[filterKey] = {
                sql_query: sqls.sql_query,
            }
        }

        if (dataType === 'recurring' && onRecurringFilter) {
            const proceedRecurring = () => {
                const config: any = {}
                if (type == 'auto') {
                    config.delta_measurement_table_name = selectedDataTable.table.name
                } else {
                    config.delta_measurement_table_name = selectedDataTable
                }
                if (payload[filterKey]) {
                    config.measurement_table_filters = payload[filterKey]
                }
                onRecurringFilter(config)
            }

            if (sqls?.sql_query) {
                experimentApi.getRecurringRequiredColumnList(
                    {
                        literal: expLiteral,
                    },
                    (resp: any) => {
                        const requiredColumns: string[] = resp?.data?.measurement_required_columns ?? []
                        const availableColumns = dataColumns ?? []
                        const missingColumns = requiredColumns.filter((col) => !availableColumns.includes(col))
                        if (missingColumns.length === 0) {
                            proceedRecurring()
                        } else {
                            const columnsMapData = experiment.experiment_metadatas.measurement_column_map
                            const columnsMapArranged = { ...columnsMapData, kpi_columns: columnsMapData?.kpi.map((kpi: any) => kpi.column_map).filter((col: string) => missingColumns.includes(col)) }
                            const { kpi, date_frequency, ...columnsMap } = columnsMapArranged

                            const missingColumnMap: any = {}
                            Object.keys(columnsMap).forEach((key) => {
                                if (typeof columnsMap[key] === 'string' && missingColumns.includes(columnsMap[key])) {
                                    missingColumnMap[key] = columnsMap[key]
                                }
                                if (Array.isArray(columnsMap[key])) {
                                    const filteredColumns = columnsMap[key].filter((col: string) => missingColumns.includes(col))
                                    if (filteredColumns.length > 0) {
                                        missingColumnMap[key] = filteredColumns
                                    }
                                }
                            })
                            setRecurringFileUploadError({
                                measurement_missing_columns: missingColumnMap,
                            })
                        }
                    },
                    (error: any) => {
                        toast.error(error?.message)
                    },
                )
                return
            }

            proceedRecurring()
            return
        }

        if (experiment?.step >= 6 && !experiment?.is_draft) {
            measurementHookFunction?.deleteInsightsWarning(() => {
                if (!filters && !sqls) {
                    payload[filterKey] = {}
                }
                updateExperimentCall(payload)
            })
        } else {
            if (!filters && !sqls) {
                payload[filterKey] = {}
            }
            updateExperimentCall(payload)
        }
    }

    const enterNl2sql = () => {
        setFilterTab('nl2sql')
        setCurrentQueryExecuted(false)
        restoreChatFilter()
    }

    const goToNl2sql = () => {
        if (filterTab === 'nl2sql' && chatRestoreStatus !== 'failed') return
        trackEvent('measurement_nl2sql_tab_selected', { experiment_literal: expLiteral })
        proceedOrPromptToSaveQuery({ currentQueryExecuted: querySavePending, dialog, t, sqlRef: { current: { saveFeatures: saveQuery } }, proceed: enterNl2sql })
    }

    const enterSql = () => {
        setFilterTab('sql')
        getFilterPreviwData()
        setFilters(null)
        setSqls(null)
    }

    const goToSql = () => {
        trackEvent('measurement_sql_tab_selected', { experiment_literal: expLiteral })
        proceedOrPromptToSaveQuery({ currentQueryExecuted: chatSave.unsaved, dialog, t, sqlRef: { current: { saveFeatures: saveQuery } }, proceed: enterSql })
    }

    const isQuerySavePending = (next: boolean = false) => {
        console.log('is query save pending called', currentQueryExecuted, blocker)
        if (querySavePending) {
            dialog.confirm({
                type: 'delete',
                title: t('Save this query for future use?'),
                message: t('Would you like to save this query as a reusable Feature for future experiments?'),
                //warning: 'By Changing this, it will alter the existing experiment details',
                cancelButtonLabel: t('No, Not Now'),
                confirmButtonLabel: t('Yes, Save Query'),
                onConfirm: () => {
                    console.log('saving features', sqlRef?.current?.saveFeatures)
                    saveQuery()
                    blocker.reset?.()
                },
                onCancel: () => {
                    if (next && filterTab === 'nl2sql') {
                        // A chat filter keeps its tab and preview, and the blocker is told this prompt was already answered.
                        skipNextBlockerPromptRef.current = true
                        updateExperiment()
                    } else if (next) {
                        updateExperiment()
                        setCurrentQueryExecuted(false)
                        changeToAddFilterTab(experiment, setFilterTab, getFilterPreviwData, setFilters, setSqls)
                        blocker.proceed?.()
                    } else {
                        console.log('changing to filter tab')
                        changeToAddFilterTab(experiment, setFilterTab, getFilterPreviwData, setFilters, setSqls)
                        blocker.proceed?.()
                    }
                },
            })
        } else {
            console.log('changing to filter tab without pending query')
            changeToAddFilterTab(experiment, setFilterTab, getFilterPreviwData, setFilters, setSqls)
        }
    }

    const getTableName = (): string => {
        if (experiment && dataType) {
            switch (dataType) {
                case 'Measurement':
                    return experiment?.data_mappings?.user_created_measurement_table_name || experiment?.data_mappings?.delta_measurement_table_name || '-'
                case 'Intervention':
                    return experiment?.data_mappings?.user_created_intervention_table_name || experiment?.data_mappings?.delta_intervention_table_name || '-'
                case 'Recommendation':
                    return experiment?.data_mappings?.user_created_recommendation_table_name || experiment?.data_mappings?.delta_recommendation_table_name || '-'
                case 'recurring':
                    return dataPreviewTable || experiment?.data_mappings?.user_created_measurement_table_name || experiment?.data_mappings?.delta_measurement_table_name || '-'
                default:
                    return ''
            }
        } else {
            return ''
        }
    }

    useEffect(() => {
        if (tableType) {
            if (experiment.experiment_metadatas?.[filterKey]?.filters) {
                const F = { filters: experiment.experiment_metadatas?.[filterKey]?.filters }
                setFilters(F)
                getFilterPreviwData(F)
            } else if (experiment.experiment_metadatas?.[filterKey]?.sql_query) {
                // Restored even when the SQL tab is hidden, since the saved query would otherwise never reach the preview.
                const S = { sql_query: experiment.experiment_metadatas?.[filterKey]?.sql_query }
                setSqls(S)
                getFilterPreviwData(S, false)
            } else if (experiment.experiment_metadatas?.[filterKey]?.sql) {
                const S = { sql_query: experiment.experiment_metadatas?.[filterKey]?.sql }
                setSqls(S)
                getFilterPreviwData(S)
            } else {
                getFilterPreviwData()
            }
        }
    }, [tableType])

    useEffect(() => {
        if (_for && experiment) {
            setTableType(dataType == 'Measurement' ? 'KPI' : dataType)
        }
        // Same as before when the chat tab is off: always start on "Add Filters".
        setFilterTab(resolveStartTab(experiment, visibleTabs, filterKey))
        setFilters(null)
        setSqls(null)
        resetChatFilter()
    }, [_for, experiment, dataType, resetChatFilter])

    useQuerySaveBlocker(blocker, isQuerySavePending, skipNextBlockerPromptRef)

    if (experimentLiteral && !fetchedExperiment) {
        return (
            <div className="flex justify-center items-center py-10">
                <Loader />
            </div>
        )
    }

    if (completed) {
        return (
            <div className="flex items-center gap-2 py-3 text-green-700">
                <span className="text-sm font-medium">&#10003; {t('Filters applied successfully')}</span>
            </div>
        )
    }

    return (
        <>
            {localLoading && (
                <div className="flex justify-center items-center py-10">
                    <Loader />
                </div>
            )}
            <div className="flex items-center justify-between">
                <div className="relative flex items-center gap-2">
                    <div className="absolute -left-5 top-0 h-full w-2 bg-primary rounded-e-md"></div>
                    <P className="!mb-0 font-semibold">{experiment?.experiment_name}</P>
                </div>
            </div>
            <p className="text-[1.1rem] font-bold mt-2">{t('{{type}} Data', { type: dataType })}</p>

            <div className="flex items-center gap-3 h-[2.667rem] border-b border-[#00000014] my-2">
                {visibleTabs.includes('nl2sql') && <FilterTabButton label={t('Chat to Filter')} active={filterTab == 'nl2sql'} onClick={goToNl2sql} testId="nl2sql-tab" />}
                {visibleTabs.includes('filter') && (
                    <FilterTabButton
                        label={t('Add Filters to your selected Data')}
                        active={filterTab == 'filter'}
                        className={`relative h-full flex items-center ${experiment?.user_design ? 'opacity-30' : 'cursor-pointer'}`}
                        onClick={() => {
                            trackEvent('measurement_filter_tab_selected', { experiment_literal: expLiteral })
                            isQuerySavePending()
                        }}
                        testId="filter-tab"
                    />
                )}
                {visibleTabs.includes('sql') && <FilterTabButton label={t('Create your own features')} active={filterTab == 'sql'} onClick={goToSql} testId="sql-tab" />}
            </div>
            {filterTab == 'nl2sql' && (
                <Nl2sqlTabLayout save={chatSave} query={sqls?.sql_query}>
                    {chatRestoreStatus === 'loading' && <Loader />}
                    {chatRestoreStatus === 'failed' && (
                        <Button theme="secondary" size="xs" onClick={enterNl2sql}>
                            {t('Try Again')}
                        </Button>
                    )}
                    {chatRestoreStatus === 'ready' && (
                        <LazyNlToSqlFilter
                            literal={expLiteral}
                            stage={dataType === 'recurring' ? 'Measurement' : dataType}
                            savedFilterSql={currentSavedSql ?? null}
                            // This component's own `experiment` is a snapshot; the store copy updates after a CSV is attached.
                            attachedTableName={reduxExperiment?.data_mappings?.additional_measurement_table_name}
                            onApplySql={(payload) => {
                                setChatApplying(true)
                                getFilterPreviwData(payload, false, () => setChatApplying(false))
                            }}
                            onAdditionalCsvUploaded={() => componentFunctions.onAdditionalCsvUploaded('AdditionalMeasurement', expLiteral || '', setAdditionalcolumnDefs, setAdditionalRowData)}
                            onPendingChange={setChatApplying}
                            onPreviewSql={previewDraft}
                        />
                    )}
                </Nl2sqlTabLayout>
            )}
            {columnPreviewData.length > 0 && (
                <>
                    {filterTab == 'filter' && <AddFilters _for={_for} tables={columnPreviewData} applyFilter={applyFilter} experimentLiteral={experimentLiteral} />}
                    {filterTab == 'sql' && (
                        <Sql
                            data-testid="sql"
                            _ref={sqlRef}
                            applySQL={applySQL}
                            currentQueryExecuted={currentQueryExecuted}
                            selectedDataTable={selectedDataTable?.table?.name || selectedDataTable}
                            setCurrentQueryExecuted={setCurrentQueryExecuted}
                            onadditionalCsvUploaded={() => {
                                componentFunctions.onAdditionalCsvUploaded('AdditionalMeasurement', expLiteral || '', setAdditionalcolumnDefs, setAdditionalRowData)
                            }}
                            type="measurement"
                            dataType={dataType}
                        />
                    )}
                </>
            )}

            <div>
                <div className="flex items-center gap-3">
                    <Text size="xl" className="my-4 font-semibold" data-testid="data-preview-title">
                        {t('Data Preview')}
                    </Text>

                    <Text size="sm" className="px-2 py-1 font-semibold bg-[#E6E5E5] rounded flex-shrink whitespace-nowrap truncate" title={getTableName()}>
                        {getTableName()}
                    </Text>
                </div>
                <DraftPreviewNotice draft={draft} />
                <div className="ag-theme-alpine">
                    <CustomAgGrid
                        key={'pdata_12_' + i18n.language}
                        gridRef={processDataGridRef}
                        rowData={displayRows(rowData)}
                        columnDefs={displayColumns(columnDefs)}
                        defaultColDef={{ sortable: false, filter: false }}
                        paginationPageSize={20} // Must match one of the values below
                        paginationPageSizeSelector={[10, 20, 50]} // Ensure 5 is included
                        style={{ height: 350 }}
                        pagination={false}
                        customPagination={true}
                        theme="light"
                    />
                </div>
            </div>

            {additionalRowData && additionalRowData.length > 0 && (
                <div>
                    <div className="flex items-center gap-3">
                        <Text size="xl" className="my-4 font-semibold" data-testid="data-preview-title">
                            {t('Additional Data Preview')}
                        </Text>
                        <Text size="sm" className="px-2 py-1 font-semibold bg-[#E6E5E5] rounded flex-shrink whitespace-nowrap truncate" title={experiment?.data_mappings?.additional_measurement_table_name}>
                            {experiment?.data_mappings?.additional_measurement_table_name ?? '-'}
                        </Text>
                    </div>
                    <div className="ag-theme-alpine">
                        <CustomAgGrid
                            key={'pdata_13_' + i18n.language}
                            gridRef={processAdditionalDataGridRef}
                            rowData={additionalRowData}
                            columnDefs={additionalcolumnDefs}
                            defaultColDef={{ sortable: false, filter: false }}
                            paginationPageSize={20}
                            paginationPageSizeSelector={[10, 20, 50]}
                            style={{ height: 350 }}
                            theme="light"
                            pagination={false}
                            customPagination={true}
                            data-testid="data-preview-grid"
                        />
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between my-4 ">
                <div className="flex items-center gap-3">
                    {!hidePreviousStep && (
                        <Button theme="secondary" styleType="outline" size="xs" onClick={prev} data-testid="previous-step-button" disabled={working}>
                            {t('Previous Step')}
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        theme="secondary"
                        type="button"
                        size="xs"
                        className="text-white"
                        disabled={rowData?.length == 0 || working || chatApplying || (filterTab === 'nl2sql' && chatRestoreStatus !== 'ready')}
                        onClick={() => {
                            if (querySavePending) {
                                isQuerySavePending(true)
                            } else {
                                updateExperiment()
                            }
                        }}
                        data-testid="next-step-button"
                    >
                        {working ? <Loader /> : t('NEXT STEP')}
                    </Button>
                </div>
            </div>

            <RecurringFileUploadErrorModal _ref={recurringFileUploadErrorModalRef} error={recurringFileUploadError} setRecurringFileUploadError={setRecurringFileUploadError} />
        </>
    )
}

export default ProcessData
